import { useWallet, useWalletModal } from "@solomon-apps/shared";
import type { SolanaAddress } from "@solomon-labs/types";
import { useRef, useState } from "react";

import {
    createClientWallet,
    issueWalletChallenge,
    WalletServiceError,
    type WalletView
} from "@/services/clientWallets";
import { walletVerificationErrorCopy } from "@/services/walletVerificationErrors";
import { resolveClientFormError } from "@/utils/clientFormErrors";
import { useWalletVerification } from "@/verification";
interface UseClientWalletVerificationResult {
    connected: boolean;
    connectedAddress: SolanaAddress | null;
    error: string | null;
    isVerifying: boolean;
    verify: (address: string, label: string) => Promise<WalletView | null>;
    clearError: () => void;
}

/** Shared by full and lite onboarding. Only the persisted server response
 * establishes verification; a local signature alone does not. */
export function useClientWalletVerification(): UseClientWalletVerificationResult {
    const { connected, publicKey } = useWallet();
    const { openModal } = useWalletModal();
    const walletVerification = useWalletVerification();
    const [error, setError] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const pending = useRef(false);
    const connectedAddress = publicKey?.toBase58() ?? null;

    async function verify(address: string, label: string): Promise<WalletView | null> {
        if (pending.current) return null;
        if (!connected) {
            openModal();
            return null;
        }
        if (!connectedAddress) return null;
        pending.current = true;
        setError(null);
        setIsVerifying(true);
        try {
            const walletAddress = address.trim();
            const challenge = await issueWalletChallenge({ walletAddress, signerAddress: connectedAddress });
            const result = await walletVerification.verify(connectedAddress, challenge.message, {
                method: "signMessage"
            });
            if (!result.proof) {
                setError(result.reason === "cancelled" ? "Verification was cancelled." : result.errorMessage);
                return null;
            }
            return await createClientWallet({
                walletAddress,
                signerAddress: connectedAddress,
                nonce: challenge.nonce,
                signature: result.proof.signature,
                ...(result.proof.signedMessage ? { signedMessage: result.proof.signedMessage } : {}),
                label: label.trim() || null
            });
        } catch (error) {
            setError(
                error instanceof WalletServiceError
                    ? walletVerificationErrorCopy(error.code)
                    : resolveClientFormError(
                          error,
                          "onboarding",
                          "Wallet verification could not be completed. Retry verification from this wallet step."
                      )
            );
            return null;
        } finally {
            pending.current = false;
            setIsVerifying(false);
        }
    }

    return { connected, connectedAddress, error, isVerifying, verify, clearError: (): void => setError(null) };
}
